import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Briefcase,
  Calendar,
  DollarSign,
  MapPin,
  Bookmark,
  X,
  Building,
  Clock,
  Users,
  Globe,
  ExternalLink
} from 'lucide-react';
import Button from '../components/ui/Button';

const internships = [
  {
    id: 1,
    role: 'Software Engineering Intern',
    company: 'Google',
    location: 'Mountain View, CA',
    type: 'Summer Internship',
    duration: '12 weeks',
    stipend: '$8,000/month',
    deadline: '2025-02-15',
    requirements: [
      'Currently pursuing BS/MS in Computer Science or related field',
      'Strong programming skills in one or more languages',
      'Experience with data structures and algorithms',
      'Strong problem-solving abilities'
    ],
    description: 'Join our team to work on cutting-edge technology projects that impact billions of users. You\'ll collaborate with experienced engineers and contribute to real-world applications.',
    skills: ['Java', 'Python', 'Algorithms', 'Data Structures', 'Git'],
  },
  {
    id: 2,
    role: 'Data Science Intern',
    company: 'Microsoft',
    location: 'Redmond, WA',
    type: 'Summer Internship',
    duration: '10 weeks',
    stipend: '$7,500/month',
    deadline: '2025-02-28',
    requirements: [
      'Currently pursuing MS/PhD in Data Science, Statistics, or related field',
      'Experience with machine learning frameworks',
      'Strong analytical and problem-solving skills',
      'Proficiency in Python and SQL'
    ],
    description: 'Work with our data science team on real-world ML problems. Help build and improve our AI models that power Microsoft\'s products.',
    skills: ['Python', 'Machine Learning', 'SQL', 'Statistics', 'TensorFlow'],
  },
  {
    id: 3,
    role: 'Product Management Intern',
    company: 'Amazon',
    location: 'Seattle, WA',
    type: 'Summer Internship',
    duration: '12 weeks',
    stipend: '$7,800/month',
    deadline: '2025-03-15',
    requirements: [
      'Currently pursuing MBA or related technical degree',
      'Strong leadership and communication skills',
      'Previous project management experience',
      'Data-driven decision making abilities'
    ],
    description: 'Drive product strategy and execution in our retail division. Work closely with engineering, design, and business teams to launch new features.',
    skills: ['Product Strategy', 'Data Analysis', 'Project Management', 'Agile'],
  },
  {
    id: 4,
    role: 'UX Design Intern',
    company: 'Apple',
    location: 'Cupertino, CA',
    type: 'Summer Internship',
    duration: '12 weeks',
    stipend: '$7,200/month',
    deadline: '2025-03-01',
    requirements: [
      'Pursuing degree in Design, HCI, or related field',
      'Strong portfolio demonstrating UI/UX projects',
      'Proficiency in design tools',
      'Understanding of user-centered design principles'
    ],
    description: 'Join Apple\'s design team to create beautiful and intuitive user experiences. Work on next-generation products and features.',
    skills: ['Figma', 'User Research', 'Prototyping', 'Visual Design', 'Adobe Creative Suite'],
  },
  {
    id: 5,
    role: 'Cloud Engineering Intern',
    company: 'AWS',
    location: 'Remote',
    type: 'Fall Internship',
    duration: '16 weeks',
    stipend: '$8,200/month',
    deadline: '2025-04-15',
    requirements: [
      'Pursuing degree in Computer Science or related field',
      'Knowledge of cloud computing concepts',
      'Experience with one or more programming languages',
      'Understanding of distributed systems'
    ],
    description: 'Work on AWS infrastructure and services. Help build and maintain scalable cloud solutions used by millions of customers.',
    skills: ['AWS', 'Cloud Computing', 'Python', 'DevOps', 'Docker'],
  },
  {
    id: 6,
    role: 'Machine Learning Intern',
    company: 'Meta',
    location: 'Menlo Park, CA',
    type: 'Summer Internship',
    duration: '12 weeks',
    stipend: '$8,000/month',
    deadline: '2025-02-20',
    requirements: [
      'Advanced degree in Machine Learning, AI, or related field',
      'Strong background in deep learning',
      'Experience with ML frameworks',
      'Research experience preferred'
    ],
    description: 'Join Meta\'s AI research team to work on cutting-edge machine learning projects. Help develop next-generation AI technologies.',
    skills: ['PyTorch', 'Deep Learning', 'Python', 'Research', 'NLP'],
  },
  {
    id: 7,
    role: 'Frontend Engineering Intern',
    company: 'Netflix',
    location: 'Los Gatos, CA',
    type: 'Summer Internship',
    duration: '12 weeks',
    stipend: '$7,500/month',
    deadline: '2025-03-10',
    requirements: [
      'Pursuing degree in Computer Science or related field',
      'Strong JavaScript/TypeScript skills',
      'Experience with modern frontend frameworks',
      'Understanding of web performance optimization'
    ],
    description: 'Work on Netflix\'s streaming interface used by millions of users. Build and optimize features for our web platform.',
    skills: ['React', 'TypeScript', 'Web Performance', 'CSS', 'Testing'],
  },
  {
    id: 8,
    role: 'Cybersecurity Intern',
    company: 'IBM',
    location: 'Remote',
    type: 'Fall Internship',
    duration: '14 weeks',
    stipend: '$6,800/month',
    deadline: '2025-04-01',
    requirements: [
      'Pursuing degree in Cybersecurity or related field',
      'Knowledge of security principles and best practices',
      'Programming experience',
      'Understanding of network protocols'
    ],
    description: 'Join IBM\'s security team to help protect enterprise systems and data. Work on threat detection and prevention.',
    skills: ['Security', 'Python', 'Network Security', 'Cryptography', 'Risk Assessment'],
  },
  {
    id: 9,
    role: 'Mobile Development Intern',
    company: 'Uber',
    location: 'San Francisco, CA',
    type: 'Summer Internship',
    duration: '12 weeks',
    stipend: '$7,600/month',
    deadline: '2025-03-05',
    requirements: [
      'Pursuing degree in Computer Science or related field',
      'Experience with mobile development',
      'Knowledge of iOS or Android platforms',
      'Understanding of mobile design patterns'
    ],
    description: 'Work on Uber\'s mobile applications. Develop new features and improve user experience for millions of users.',
    skills: ['iOS', 'Android', 'Swift', 'Kotlin', 'Mobile Architecture'],
  },
  {
    id: 10,
    role: 'Blockchain Development Intern',
    company: 'Coinbase',
    location: 'Remote',
    type: 'Summer Internship',
    duration: '12 weeks',
    stipend: '$7,000/month',
    deadline: '2025-03-20',
    requirements: [
      'Pursuing degree in Computer Science or related field',
      'Understanding of blockchain technology',
      'Experience with smart contracts',
      'Knowledge of cryptography principles'
    ],
    description: 'Join Coinbase to work on blockchain and cryptocurrency projects. Help build the future of digital finance.',
    skills: ['Blockchain', 'Solidity', 'Smart Contracts', 'Web3', 'DeFi'],
  }
];

const Internships: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [bookmarkedInternships, setBookmarkedInternships] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showBookmarked, setShowBookmarked] = useState(false);

  const filteredInternships = internships.filter((internship) => {
    const matchesSearch =
      internship.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      internship.company.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      selectedTypes.length === 0 || selectedTypes.includes(internship.type);

    const matchesBookmarked = 
      !showBookmarked || bookmarkedInternships.includes(internship.id);

    return matchesSearch && matchesType && matchesBookmarked;
  });

  const toggleBookmark = (id: number) => {
    setBookmarkedInternships((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const clearFilters = () => {
    setSelectedTypes([]);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Hero Section */}
          <div className="relative bg-gradient-to-b from-primary-900 to-primary-800 py-16 mb-12 rounded-3xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-700 rounded-full mix-blend-multiply opacity-20 animate-blob"></div>
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute top-40 left-1/2 transform -translate-x-1/2 w-80 h-80 bg-accent-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
                  Find Your Perfect Internship
                </h1>
                <p className="text-xl text-primary-100 max-w-2xl mx-auto mb-8">
                  Discover internship opportunities at top companies and kickstart your career journey.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Briefcase className="h-5 w-5 mr-2" />
                    <span>Top Companies</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <DollarSign className="h-5 w-5 mr-2" />
                    <span>Competitive Pay</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Calendar className="h-5 w-5 mr-2" />
                    <span>Flexible Duration</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                {/* Search Input */}
                <div className="flex-1 w-full">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      placeholder="Search internships by role or company..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input pl-12 w-full bg-gray-50 border-0 focus:ring-2"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 w-full md:w-auto">
                  <Button
                    variant={showBookmarked ? 'primary' : 'outline'}
                    onClick={() => setShowBookmarked(!showBookmarked)}
                    icon={<Bookmark className="h-4 w-4" />}
                    className="flex-1 md:flex-none"
                  >
                    Saved
                  </Button>
                  <Button
                    variant={showFilters ? 'primary' : 'outline'}
                    onClick={() => setShowFilters(!showFilters)}
                    icon={<Filter className="h-4 w-4" />}
                    className="flex-1 md:flex-none"
                  >
                    Filters
                  </Button>
                </div>
              </div>

              {/* Filter Section */}
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-6 pt-6 border-t"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                    {selectedTypes.length > 0 && (
                      <button
                        onClick={clearFilters}
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Clear all filters
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {['Summer Internship', 'Fall Internship', 'Winter Internship', 'Co-op'].map((type) => (
                      <button
                        key={type}
                        onClick={() =>
                          setSelectedTypes((prev) =>
                            prev.includes(type)
                              ? prev.filter((t) => t !== type)
                              : [...prev, type]
                          )
                        }
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                          selectedTypes.includes(type)
                            ? 'bg-primary-100 text-primary-800 ring-1 ring-primary-500'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {filteredInternships.length} {filteredInternships.length === 1 ? 'Internship' : 'Internships'} Available
              </h2>
              <div className="text-sm text-gray-500">
                Updated daily
              </div>
            </div>
          </motion.div>

          {/* Internship Cards */}
          <motion.div
            variants={containerVariants}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredInternships.map((internship) => (
              <motion.div
                key={internship.id}
                variants={itemVariants}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-[2rem] shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full"
              >
                <div className="p-8 flex flex-col flex-grow">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {internship.role}
                      </h3>
                      <div className="flex items-center text-gray-500">
                        <Building className="h-4 w-4 mr-2" />
                        <span className="font-medium">{internship.company}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleBookmark(internship.id)}
                      icon={bookmarkedInternships.includes(internship.id) ? 
                        <Bookmark className="h-4 w-4" fill="currentColor" /> : 
                        <Bookmark className="h-4 w-4" />
                      }
                      className="rounded-full"
                    >
                      {bookmarkedInternships.includes(internship.id) ? 'Saved' : 'Save'}
                    </Button>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 mb-6">
                    {internship.description}
                  </p>

                  {/* Key Details */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                      {internship.location}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Clock className="h-4 w-4 mr-2 text-gray-400" />
                      {internship.duration}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <DollarSign className="h-4 w-4 mr-2 text-gray-400" />
                      {internship.stipend}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {new Date(internship.deadline).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Skills */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Required Skills:</h4>
                    <div className="flex flex-wrap gap-2">
                      {internship.skills.map((skill) => (
                        <span
                          key={skill}
                          className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Apply Button - Always at the bottom */}
                <div className="px-8 pb-8">
                  <Button
                    fullWidth
                    icon={<ExternalLink className="h-4 w-4" />}
                    iconPosition="right"
                    className="rounded-full"
                  >
                    Apply Now
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Empty State */}
          {filteredInternships.length === 0 && (
            <motion.div
              variants={itemVariants}
              className="text-center py-16 bg-white rounded-2xl shadow-md"
            >
              <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No internships found</h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your filters or search terms to find more opportunities.
              </p>
              <Button
                variant="outline"
                onClick={clearFilters}
                icon={<X className="h-4 w-4" />}
              >
                Clear all filters
              </Button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Internships;